import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AgentGroup } from 'src/app/models/agentGroup.model';
import { Lookup } from 'src/app/models/lookup.model';
import { AgentGroupService } from 'src/app/services/agentGroup.service';
import { ColumnSpan } from 'src/app/shared/form/columnSpan.model';
import { FormComponent, IFormDialogData } from 'src/app/shared/form/form.component';
import { ButtonFormField, RadioFormField, TextFormField } from 'src/app/shared/form/formField.model';
import nameOf from 'src/app/shared/form/nameOf.helper';

export interface IAgentToGroupDialog extends IFormDialogData {
    agentIds: string[];
}

@Component({
  standalone: false,
    selector: "app-agent-to-group-dialog",
    templateUrl: "./../../shared/form/form.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        "./../../shared/form/form.component.scss",
        "agent-to-group-dialog.component.scss",
    ],
})
export class AgentToGroupDialogComponent
    extends FormComponent<AgentGroup>
    implements OnInit
{
    agentGroupsList: Lookup[] = [];
    filteredAgentGroups: Lookup[] = [];

    constructor(
        dialogRef: MatDialogRef<AgentToGroupDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: IAgentToGroupDialog,
        protected agentGroupService: AgentGroupService,
        protected injector: Injector,
    ) {
        super(dialogRef, agentGroupService, data, "Agent", injector);
        this.title = "Add to Group";
    }

    ngOnInit(): void {
        this.isFormLoading = true;
        forkJoin({
            entity: this.getEntity(),
            agentGroups: this.agentGroupService.getAll(),
        }).subscribe(
            (result) => {
                this.entity = result.entity;
                this.agentGroupsList = result.agentGroups;
                this.filteredAgentGroups = [...result.agentGroups];
                this.initFields();
                this.isFormLoading = false;
            },
            (error) => {
                this.toastService.showError(error);
                this.isFormLoading = false;
            },
        );
    }

    getNewObject(): AgentGroup {
        return new AgentGroup();
    }

    validate(): boolean {
        return true;
    }

    initFields(): void {
        this.fields = [
            new TextFormField({
                property: "agentGroupSearchField",
                name: "search agent groups",
                columnSpan: new ColumnSpan(4, 6),
                cssClass: "search-agent-group",
                required: false,
            }),
            new ButtonFormField({
                property: "searchButton",
                name: "",
                iconClass: "fa fa-search",
                columnSpan: new ColumnSpan(1, 6),
                cssClass: "pt-2",
                required: false,
                onClick: () => {
                    const keyword =
                        this.entity.agentGroupSearchField?.toLowerCase() || "";
                    this.filteredAgentGroups = [
                        ...this.agentGroupsList.filter((x) =>
                            x.name?.toLowerCase().includes(keyword),
                        ),
                    ];
                    const radioField = this.fields.find(
                        (f) => f.property === nameOf<AgentGroup>((x) => x.id),
                    ) as RadioFormField;
                    if (radioField) {
                        radioField.options = this.filteredAgentGroups;
                    }
                },
            }),
            new RadioFormField({
                property: nameOf<AgentGroup>((x) => x.id),
                name: "",
                cssClass: "vertical-radio-options",
                options: this.filteredAgentGroups,
                columnSpan: new ColumnSpan(1, 1),
            }),
        ];
    }

    save() {
        if (!this.validate()) return;
        this.isLoading = true;
        this.service.getById(this.entity.id).pipe(
            switchMap(res => this.service.update({ ...res, agentIds: this.data.agentIds }))
        ).subscribe({
            next: (result) => this.afterSaveAction(result),
            error: this.handleError
        });
    }
}
